const DEFAULT_BUFFER_SIZE = 4096;
const DEFAULT_TARGET_SAMPLE_RATE = 16000;

const resampleLinear = (input, inputRate, outputRate) => {
  if (inputRate === outputRate) {
    return input;
  }

  const ratio = outputRate / inputRate;
  const outputLength = Math.max(1, Math.round(input.length * ratio));
  const output = new Float32Array(outputLength);

  for (let i = 0; i < outputLength; i += 1) {
    const index = i / ratio;
    const i0 = Math.floor(index);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = index - i0;
    output[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }

  return output;
};

const float32ToInt16 = (input) => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, input[i]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
};

export default class PcmAudioRecorder {
  constructor({ targetSampleRate = DEFAULT_TARGET_SAMPLE_RATE, onAudio } = {}) {
    this.targetSampleRate = targetSampleRate;
    this.onAudio = onAudio;

    this.audioContext = null;
    this.sourceNode = null;
    this.processorNode = null;
    this.gainNode = null;
    this.stream = null;
    this.mediaStreamTrack = null;
    this.isRecording = false;
  }

  async start({ mediaStreamTrack, onAudio } = {}) {
    if (!mediaStreamTrack) {
      console.log("🛠️ PcmAudioRecorder start skipped - no mediaStreamTrack");
      return;
    }

    if (this.isRecording && this.mediaStreamTrack === mediaStreamTrack) {
      return;
    }

    this.stop();

    this.mediaStreamTrack = mediaStreamTrack;
    this.onAudio = onAudio || this.onAudio;

    console.log("🛠️ PcmAudioRecorder starting", {
      trackId: mediaStreamTrack?.id,
      trackReadyState: mediaStreamTrack?.readyState,
      trackMuted: mediaStreamTrack?.muted,
    });

    const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextImpl) {
      throw new Error("Web Audio API not supported");
    }

    this.stream = new MediaStream([mediaStreamTrack]);
    this.audioContext = new AudioContextImpl();

    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);
    this.processorNode = this.audioContext.createScriptProcessor(DEFAULT_BUFFER_SIZE, 1, 1);
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = 0;

    let frameCount = 0;
    this.processorNode.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      if (!input || input.length === 0) {
        return;
      }

      const resampled = resampleLinear(
        input,
        this.audioContext.sampleRate,
        this.targetSampleRate
      );
      const int16 = float32ToInt16(resampled);

      if (this.onAudio) {
        this.onAudio(int16.buffer);
      }

      frameCount += 1;
      if (frameCount % 20 === 0) {
        console.log("🛠️ PcmAudioRecorder audio frame", {
          inputLength: input.length,
          resampledLength: resampled.length,
          sampleRate: this.audioContext.sampleRate,
        });
      }
    };

    this.sourceNode.connect(this.processorNode);
    this.processorNode.connect(this.gainNode);
    this.gainNode.connect(this.audioContext.destination);

    if (this.audioContext.state === "suspended") {
      try {
        await this.audioContext.resume();
      } catch {
        // noop
      }
    }

    this.isRecording = true;
  }

  stop() {
    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode.onaudioprocess = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
    }

    if (this.gainNode) {
      this.gainNode.disconnect();
    }

    if (this.audioContext) {
      this.audioContext.close();
    }

    this.audioContext = null;
    this.sourceNode = null;
    this.processorNode = null;
    this.gainNode = null;
    this.stream = null;
    this.mediaStreamTrack = null;
    this.isRecording = false;
  }
}
